import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, KeyRound, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { AuthCard, inputCls } from "@/pages/ForgotPassword";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useLang();
  const { persist } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ checking: true, valid: false, hint: "" });
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setState({ checking: false, valid: false, hint: "" }); return; }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setState({ checking: false, valid: true, hint: data.email_hint }))
      .catch(() => setState({ checking: false, valid: false, hint: "" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { setError(t("passwordsMismatch")); return; }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      persist(data);
      toast.success(data.message);
      const u = data.user;
      navigate(["admin", "super_admin"].includes(u.role) ? "/admin" : u.assessment_completed ? "/app/home" : u.onboarded ? "/assessment" : "/onboarding");
    } catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };

  if (state.checking) return <AuthCard><Loader2 className="mx-auto my-16 h-8 w-8 animate-spin text-indigo-600" /></AuthCard>;

  if (!state.valid) {
    return (
      <AuthCard>
        <div data-testid="reset-invalid">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-100 text-rose-700"><KeyRound className="h-7 w-7" /></div>
          <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight">{t("resetLinkInvalid")}</h1>
          <p className="mt-2 text-muted-foreground">Reset links work once and expire after 1 hour.</p>
          <Link to="/forgot-password" data-testid="request-new-link" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-bold text-white hover:bg-indigo-700">{t("requestNewLink")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700"><ShieldCheck className="h-7 w-7" /></div>
      <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight">{t("resetTitle")}</h1>
      <p className="mt-2 text-muted-foreground">{t("resetSub")}</p>
      {state.hint && <p className="mt-1 text-xs text-muted-foreground" data-testid="reset-email-hint">Account: <span className="font-mono">{state.hint}</span></p>}
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="text-sm font-semibold">{t("newPassword")}</label>
          <input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} autoComplete="new-password" placeholder="••••••••" className={inputCls} />
        </div>
        <div>
          <label className="text-sm font-semibold">{t("confirmPassword")}</label>
          <input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} autoComplete="new-password" placeholder="••••••••" className={inputCls} />
        </div>
        {error && <p data-testid="reset-error" className="text-sm font-medium text-destructive">{error}</p>}
        <button type="submit" disabled={loading} data-testid="reset-submit-btn"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3.5 font-bold text-white transition-all hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-60">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t("updatePassword")} <ArrowRight className="h-4 w-4" /></>}
        </button>
      </form>
    </AuthCard>
  );
}
